import { FormDialog, FormDialogContent, FormDialogHeader, FormDialogTitle, FormDialogBody, FormDialogFooter } from '../../components/ui/form-dialog';
// apps/web/src/pages/cadastros/cadastro-form.tsx — Fase 03, Plano 02 (CAD-01, CAD-03).
import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { CampoSelect } from '../../components/ui/campo-select';

export interface CadastroFieldConfig {
  name: string;
  label: string;
  type?: 'text' | 'number' | 'select';
  placeholder?: string;
  required?: boolean;
  options?: Array<{ label: string; value: string }>;
  /** Tipo enviado a API (montarPayloadCadastro); o formulario so mostra texto. */
  valueType?: 'text' | 'number' | 'boolean';
  /** Campo que so existe na criacao (nao esta no Atualizar*Dto): somente leitura na edicao. */
  apenasNaCriacao?: boolean;
  /** Mensagem de campo obrigatorio vazio; padrao 'Campo obrigatório'. */
  requiredMessage?: string;
}

export type CadastroValor = string | number | boolean | null | undefined;

function normalizarValoresIniciais(valores: object): Record<string, CadastroValor> {
  return Object.fromEntries(
    Object.entries(valores).filter(([, valor]) =>
      valor === null || ['string', 'number', 'boolean', 'undefined'].includes(typeof valor),
    ),
  );
}

function valorParaInput(valor: CadastroValor): string | number {
  return typeof valor === 'boolean' ? String(valor) : valor ?? '';
}

interface CadastroFormProps {
  title: string;
  fields: CadastroFieldConfig[];
  initialValues?: object;
  modo?: 'criar' | 'editar';
  onClose: () => void;
  onSave: (values: Record<string, CadastroValor>) => Promise<void>;
}

export const CadastroForm: React.FC<CadastroFormProps> = ({
  title,
  fields,
  initialValues = {},
  modo = 'criar',
  onClose,
  onSave,
}) => {
  const [formValues, setFormValues] = useState<Record<string, CadastroValor>>(() => normalizarValoresIniciais(initialValues));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Em 'editar', campos apenasNaCriacao ficam so leitura e nao sao validados como obrigatorios.
  const somenteLeitura = (field: CadastroFieldConfig) => modo === 'editar' && Boolean(field.apenasNaCriacao);

  const validateField = (field: CadastroFieldConfig, value: CadastroValor) => {
    if (field.required && !somenteLeitura(field) && (!value || String(value).trim() === '')) {
      return field.requiredMessage ?? 'Campo obrigatório';
    }
    return '';
  };

  const handleChange = (field: CadastroFieldConfig, value: CadastroValor) => {
    setFormValues((prev) => ({ ...prev, [field.name]: value }));
    const errorMsg = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field.name]: errorMsg }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);

    // Validate all fields
    const currentErrors: Record<string, string> = {};
    let hasError = false;

    fields.forEach((f) => {
      const err = validateField(f, formValues[f.name]);
      if (err) {
        currentErrors[f.name] = err;
        hasError = true;
      }
    });

    if (hasError) {
      setErrors(currentErrors);
      return;
    }

    setSaving(true);
    try {
      await onSave(formValues);
      onClose();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Falha ao salvar dados');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <FormDialogContent aria-describedby={undefined}>
        <FormDialogHeader>
          <FormDialogTitle>{title}</FormDialogTitle>

        </FormDialogHeader>

        <form onSubmit={(event) => void handleSubmit(event)} >
          <FormDialogBody className="space-y-4">
          {serverError && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-md text-xs text-rose-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{serverError}</span>
            </div>
          )}

          {fields.map((field) => (
            <div key={field.name} className="space-y-1">
              <label htmlFor={'cadastro-' + field.name} className="block text-sm font-semibold text-slate-700">
                {field.label} {field.required && !somenteLeitura(field) && <span className="text-rose-500">*</span>}
              </label>

              {field.type === 'select' ? (
                <CampoSelect
                  id={'cadastro-' + field.name}
                  aria-invalid={Boolean(errors[field.name])}
                  aria-describedby={errors[field.name] ? 'erro-' + field.name : undefined}
                  value={String(valorParaInput(formValues[field.name]))}
                  disabled={somenteLeitura(field)}
                  onValueChange={(valor) => handleChange(field, valor)}
                  options={[{ value: '', label: 'Selecione...' }, ...(field.options ?? []).map((opt) => ({ value: opt.value, label: opt.label }))]}
                />
              ) : (
                <input
                  id={'cadastro-' + field.name}
                  aria-invalid={Boolean(errors[field.name])}
                  aria-describedby={errors[field.name] ? 'erro-' + field.name : undefined}
                  type={field.type ?? 'text'}
                  value={valorParaInput(formValues[field.name])}
                  placeholder={field.placeholder}
                  readOnly={somenteLeitura(field)}
                  onChange={(e) => handleChange(field, e.target.value)}
                  onBlur={() => {
                    const err = validateField(field, formValues[field.name]);
                    setErrors((prev) => ({ ...prev, [field.name]: err }));
                  }}
                  className={`w-full px-3 py-2 border rounded-md text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 read-only:bg-slate-50 read-only:text-slate-500 ${
                    errors[field.name] ? 'border-rose-400 bg-rose-50/30' : 'border-slate-300'
                  }`}
                />
              )}

              {field.type === 'select' && field.placeholder && (
                <p className="text-[11px] text-slate-500">{field.placeholder}</p>
              )}

              {errors[field.name] && (
                <p id={'erro-' + field.name} role="alert" className="text-sm text-rose-600 font-medium">{errors[field.name]}</p>
              )}
            </div>
          ))}

          </FormDialogBody>
          <FormDialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-slate-900 text-white rounded-md text-xs font-semibold hover:bg-slate-800 disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </FormDialogFooter>
        </form>
      </FormDialogContent>
    </FormDialog>
  );
};
